import { Group, Stack, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { Eyebrow } from '@/components/Eyebrow';
import { useFormat } from '@/components/useFormat';
import { shutterDenominator } from '@/planner/stats';
import type { Plan } from '@/planner/types';
import { brand } from '@/theme/theme';

/** The plan's figures as a dense label–value list for the side panel (spec §6.3). */
export function StatsList({ plan, dtmKnown }: { plan: Plan; dtmKnown: boolean }) {
  const { t } = useTranslation('missions');
  const { number } = useFormat();
  const s = plan.stats;
  const minGap = plan.photo_gaps_s.length ? Math.min(...plan.photo_gaps_s) : null;
  const clearance = !dtmKnown || s.clearance_min_m === null || s.clearance_max_m === null ? '—' : t('stats.clearanceRange', { min: number(s.clearance_min_m), max: number(s.clearance_max_m) });
  const rows: [string, string][] = [
    [t('stats.area'), `${number(s.area_ha, 1)}\u00a0ha`],
    [t('stats.gsd'), `${number(s.gsd_cm, 2)}\u00a0cm/px`],
    [t('stats.lines'), number(s.lines)],
    [t('stats.photos'), number(s.photos)],
    [t('stats.spacing'), `${number(s.line_spacing_m, 1)} / ${number(s.photo_spacing_m, 1)}\u00a0m`],
    [t('stats.path'), s.path_m >= 1000 ? `${number(s.path_m / 1000, 2)}\u00a0km` : `${number(s.path_m)}\u00a0m`],
    [t('stats.speed'), `${number(plan.speed_ms, 1)}\u00a0m/s`],
    [t('stats.minGap'), minGap === null ? '—' : `${number(minGap, 1)}\u00a0s`],
    [t('stats.shutter'), `1/${number(shutterDenominator(plan.speed_ms, s.gsd_cm))}\u00a0s`],
    [t('stats.flightTime'), `${number(Math.round(s.flight_s / 60))}\u00a0min`],
    [t('stats.clearance'), clearance],
  ];
  return (
    <Stack gap={4} data-testid="stats-list">
      <Eyebrow>{t('stats.title')}</Eyebrow>
      {rows.map(([label, value]) => (
        <Group key={label} justify="space-between" gap="xs" wrap="nowrap">
          <Text fz={13} c={brand.textMuted}>
            {label}
          </Text>
          <Text fz={13} fw={600} ta="right">
            {value}
          </Text>
        </Group>
      ))}
    </Stack>
  );
}
